import { Injectable } from '@angular/core';
import { Login } from '../model/login';
import {UserRole} from '../model/role.enum';

const LOGIN_INFO_KEY = 'login-info';

export interface JwtPayload {
  id: number;
  roles: string[];
  sub: string;
  iat: number;
  exp: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {

  logout(): void {
    window.sessionStorage.clear();
  }

  public saveLoginInfo(loginInfo: Login): void {
    window.sessionStorage.removeItem(LOGIN_INFO_KEY);
    window.sessionStorage.setItem(LOGIN_INFO_KEY, JSON.stringify(loginInfo));
  }

  public getToken(): string | undefined {
    return this.getLoginInfo()?.token;
  }

  public getLoginInfo(): Login | undefined {
    const loginInfo = window.sessionStorage.getItem(LOGIN_INFO_KEY);
    if (loginInfo) {
      return JSON.parse(loginInfo);
    }
    return undefined;
  }

  public decodeToken(): JwtPayload | null {
    const token = this.getToken();
    if (!token) return null;
    return this.decodeJwtToken(token);
  }

  public isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const payload = this.decodeToken();
    if (!payload) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp > currentTime;
  }

  public getRoles(): string[] {
    const payload = this.decodeToken();
    return payload?.roles || [];
  }

  public hasRole(role: UserRole | string): boolean {
    const roles = this.getRoles();
    return roles.includes(role.toString().toUpperCase());
  }

  public hasAnyRole(roles: UserRole[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  public hasAllRoles(roles: UserRole[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  public canAccessVouchers(): boolean {
    return this.hasAnyRole([UserRole.ADMIN, UserRole.VOUCHER_MANAGER]);
  }

  public canAccessAdmin(): boolean {
    return this.hasRole(UserRole.ADMIN);
  }

  public isOnlyVoucherManager(): boolean {
    return this.hasRole(UserRole.VOUCHER_MANAGER) && !this.hasRole(UserRole.ADMIN);
  }

  public getPrimaryRole(): string {
    if (this.hasRole(UserRole.ADMIN)) return 'Администратор';
    if (this.hasRole(UserRole.VOUCHER_MANAGER)) return 'Менеджер ваучеров';
    return 'Пользователь';
  }

  public getUserId(): number | null {
    const payload = this.decodeToken();
    return payload?.id ?? null;
  }

  public getUsername(): string | null {
    const payload = this.decodeToken();
    return payload?.sub ?? null;
  }

  public getExpirationTime(): Date | null {
    const payload = this.decodeToken();
    return payload ? new Date(payload.exp * 1000) : null;
  }

  public getIssuedAtTime(): Date | null {
    const payload = this.decodeToken();
    return payload ? new Date(payload.iat * 1000) : null;
  }

  public getTimeUntilExpiration(): number | null {
    const payload = this.decodeToken();
    if (!payload) return null;

    const currentTime = Math.floor(Date.now() / 1000);
    return Math.max(0, payload.exp - currentTime);
  }

  public shouldRefreshToken(): boolean {
    const timeUntilExpiration = this.getTimeUntilExpiration();
    if (!timeUntilExpiration) return false;

    return timeUntilExpiration < 300;
  }

  private decodeJwtToken(token: string): JwtPayload | null {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payloadBase64 = parts[1];
      let base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      const padLength = (4 - (base64.length % 4)) % 4;
      base64 += '='.repeat(padLength);

      const decodedJson = atob(base64);
      return JSON.parse(decodedJson);
    } catch (error) {
      return null;
    }
  }
}
